import NextImage from 'next/image'
import { STATIC_IMAGE_BASE } from '@mc/constants'
import { useTranslation } from '@mc/i18n'
import { getNumberOfInstructors } from '@mc/instructors'
import { useStaticData } from '@mc/static-data-react'
import { Locations } from '@mc/track-event'
import CTA from 'components/CTA'
import {
  DefaultNameplate as DesktopDefaultNameplate,
  AlternativeNameplate,
} from 'components/HighContrastHero/components/HeroContent'
import Logo from 'components/Navigation/MCLogo/assets/logo.svg'
import { PricingText } from 'components/PricingText'
import { PrunedCourse } from 'libs/server/api'
import { CM_TIMELINE_VIEW_V2_I18N_NAMESPACE } from '../../../../i18n'
import { DefaultNameplateMobile } from './DefaultNameplateMobile'
import { TrailerAction } from './TrailerAction'
import { Data } from './types'
import { getChaptersAndDuration } from './utils'
import classes from './Hero.module.scss'

// same as CTASection of web/apps/next/src/components/HighContrastHero/components/HeroContent.tsx
const finalTrackProps = {
  category: 'video-playlist-module',
  content_type: 'product',
  location: Locations.HERO,
}

export const CourseDataTrailerAction = () => {
  const { t } = useTranslation(CM_TIMELINE_VIEW_V2_I18N_NAMESPACE)
  const { course } = useStaticData<{
    course: PrunedCourse
  }>()
  const { chaptersCount, courseDuration } = getChaptersAndDuration(course)

  const trailer = {
    brightcoveVideoId: course.brightcoveVideoId,
    mediaUuid: course.mediaUuid,
    instructorName: course.instructorName,
    headline: course.headline,
    id: course.id,
    slug: course.slug,
    title: course.title,
    instructorAvatar: course.primary1x1,
  }

  return (
    <div className='d-flex align-items-center justify-content-center flex-gap-2 mc-mt-4'>
      <p className='mc-text-small'>
        {t('variant_1.hero.lessons', {
          lessonsCount: chaptersCount,
        })}
        <span style={{ opacity: 0.5 }}>{` · ${courseDuration}`}</span>
      </p>
      <TrailerAction trailer={trailer} />
    </div>
  )
}

export const PricingCTASection = () => {
  const { t } = useTranslation(CM_TIMELINE_VIEW_V2_I18N_NAMESPACE)

  return (
    <div
      className={`mc-px-6 mc-py-4 mc-mt-8 flex-shrink-0 mc-text--center ${classes.pricingContainer}`}
    >
      <NextImage
        src={`${STATIC_IMAGE_BASE}/CM_instructors_hero_2x.png`}
        alt=''
        width={183}
        height={38}
      />
      <p
        className={`body mc-mt-1 mc-text--bold ${classes.pricingContainer__text}`}
      >
        {t('variant_1.hero.allClasses', {
          instructors: getNumberOfInstructors(),
        })}
      </p>
      <CTA fullWidth trackProps={finalTrackProps} className='mc-mt-4 mc-mb-2' />
      <PricingText translationKey='shortPriceStyled'>
        <span className='mc-opacity--muted' />
        <span className='mc-text--bold' />
      </PricingText>
    </div>
  )
}

export const ContentDesktop = ({ data }: { data: Data }) => {
  const { course } = useStaticData<{
    course: PrunedCourse
  }>()

  return (
    <div className='container'>
      <div className='mc-text--center'>
        <div className='row justify-content-center'>
          <div className='col-9'>
            {data.isDynamicNameplate ? (
              <AlternativeNameplate
                nameplate={data.nameplate}
                header={data.header}
                showSeparator={true}
              />
            ) : (
              <DesktopDefaultNameplate
                header={data.header}
                nameplate={data.nameplate || ''}
                namePlateSize={220}
                showSeparator={true}
                nameplateAlt={course.instructorName}
              />
            )}
          </div>
        </div>
        <div className='row d-flex justify-content-center no-gutters'>
          <div
            className='col-md-11 col-lg-10 mc-text-small'
            style={{ zIndex: 1 }}
          >
            <div className='mc-mt-3'>{data.subheader}</div>
          </div>
          <div className='col-12'>
            <CourseDataTrailerAction />
            <PricingCTASection />
          </div>
        </div>
      </div>
    </div>
  )
}

export const ContentMobile = ({ data }: { data: Data }) => (
  <div className='container'>
    <div className='row justify-content-between align-items-end'>
      <div className='col-12 d-flex flex-column align-items-start mc-text--left'>
        <Logo className='mc-icon mc-icon--3 mc-text-color--primary' />
        <DefaultNameplateMobile data={data} />
        <p className='mc-opacity--muted mc-text-small'>{data.subheader}</p>
        <CourseDataTrailerAction />
      </div>

      <PricingCTASection />
    </div>
  </div>
)
